<template>
  <transition name="fade-overlay">
    <div
      v-if="isOverlayVisible"
      class="overlay-footer-locale"
      :class="{ 'overlay-footer-locale--visible': isOverlayVisible }"
      v-data-test-id="'overlay'"
    >
      <span
        class="overlay-footer-locale__close overlay-footer-locale__close--outer icon-cross"
        v-data-test-id="'overlay-top-close'"
        @click.stop="closeOverlay"
      ></span>
      <div class="overlay-footer-locale__inner" @click.stop="closeOverlay">
        <div class="overlay-footer-locale__content" @click.stop>
          <span
            class="overlay-footer-locale__close overlay-footer-locale__close--inner icon-cross"
            v-data-test-id="'overlay-top-close'"
            @click="closeOverlay"
          ></span>
          <div class="overlay-footer-locale__title">
            {{ $t('title') }}
          </div>
          <!-- Country -->
          <div class="overlay-footer-locale__country">
            <VSelect
              v-model="selectedCountryInternal"
              :options="countryOptions"
              :components="{ OpenIndicator }"
              v-data-test-id="'country-options'"
            >
              <template #selected-option="option">
                <div class="overlay-footer-locale__option-container">
                  <span
                    class="overlay-footer-locale__flag"
                    :style="getFlagBackgroundStyle(option)"
                  ></span>
                  <span v-data-test-id="'country-option'">{{ option.label }}</span>
                </div>
              </template>

              <template v-slot:option="option">
                <div
                  class="overlay-footer-locale__item"
                  :class="{
                    'overlay-footer-locale__item--separator': option.isLastTopRevenueCountry,
                  }"
                >
                  <span
                    class="overlay-footer-locale__flag"
                    :style="getFlagBackgroundStyle(option)"
                  ></span>
                  {{ option.label }}
                </div>
              </template>
            </VSelect>
          </div>

          <!-- Language -->
          <div class="overlay-footer-locale__language">
            <VSelect
              v-model="language"
              :options="languageOptions"
              :components="{ OpenIndicator }"
              :searchable="false"
            >
              <template #selected-option="option">
                <span>{{ option.label }}</span>
              </template>

              <template v-slot:option="option">
                <span>{{ option.label }}</span>
              </template>
            </VSelect>
          </div>
          <Button
            class="overlay-footer-locale__submit"
            :color="BUTTON_STYLES.color.white"
            :isSubmitting="isSubmitting"
            v-data-test-id="'submit-button'"
            @click.prevent="loadPage"
            >{{ $t('submit') }}</Button
          >
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import VSelect from 'vue-select'
import { LANGUAGE_CODES as AVAILABLE_LANGUAGES } from '@/config/constants'
import { HIGH_REVENUE_COUNTRY_CODES } from '@/config/constants/countries'
import { getPathFromUrl } from '@/helpers/url'
import { KEY_ESC } from '@/helpers/keyCodes'
import OpenIndicator from '@/components/_icons/IconChevronDown.svg'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'

export default {
  name: 'OverlayFooterLocale',
  components: { Button, VSelect },
  props: {
    isOverlayVisible: {
      type: Boolean,
    },

    selectedCountry: {
      type: Object,
      required: true,
    },

    selectedLanguage: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      BUTTON_STYLES,
      countryNames: [],
      filteredProduct: {},
      HIGH_REVENUE_COUNTRY_CODES,
      isProductDataReady: false,
      isSubmitting: false,
      OpenIndicator,
      selectedCountryInternal: '',
      language: {},
    }
  },

  computed: {
    ...mapGetters({
      locale: 'page/locale',
      country: 'page/country',
    }),

    countryOptions() {
      return this.sortedCountryCodes.map((country, index) => {
        return {
          label: this.countryNames[country],
          code: country,
          isLastTopRevenueCountry: index === HIGH_REVENUE_COUNTRY_CODES.length - 1,
        }
      })
    },

    lowRevenueCountryCodes() {
      return Object.keys(this.countryNames).filter(
        country => !HIGH_REVENUE_COUNTRY_CODES.includes(country)
      )
    },

    sortedCountryCodes() {
      return [...HIGH_REVENUE_COUNTRY_CODES, ...this.lowRevenueCountryCodes]
    },

    languageOptions() {
      return AVAILABLE_LANGUAGES.map(language => {
        return {
          label: this.$t(`locales.${language}`),
          code: language,
        }
      })
    },

    destinationLocation() {
      const path = getPathFromUrl(window.location.pathname) || ''
      const origin = window.location.origin
      return `${origin}/${this.language.code}-${this.selectedCountryInternal.code}${path}`
    },
  },

  watch: {
    selectedLanguage: {
      immediate: true,
      async handler(language) {
        if (!language) return
        this.updateLanguage()

        this.countryNames = await this.getCountryNames(language)
      },
    },

    selectedCountry: {
      immediate: true,
      async handler(country) {
        if (!country) return
        this.selectedCountryInternal = country
      },
    },

    isOverlayVisible(isVisible) {
      this.toggleDocumentOverflow(!isVisible)
    },
  },

  created() {
    window.addEventListener('keydown', this.closeOnEsc)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.closeOnEsc)
  },

  methods: {
    ...mapActions({
      toggleDocumentOverflow: 'page/toggleDocumentOverflow',
    }),

    async getCountryNames(language) {
      const { default: countryNames } = await import(
        /* webpackChunkName: "[request]" */ `country-names/${language}`
      )
      return countryNames
    },

    updateLanguage() {
      this.language = {
        label: this.$t(`locales.${this.selectedLanguage}`),
        code: this.selectedLanguage,
      }
    },

    closeOverlay() {
      this.$emit('close-overlay')
    },

    closeOnEsc(e) {
      if (e.keyCode === KEY_ESC && this.isOverlayVisible) this.closeOverlay()
    },

    getFlagSrc(countryCode) {
      return countryCode ? require(`@/assets/images/flags/${countryCode}.svg`) : ''
    },

    getFlagBackgroundStyle(option) {
      return `background-image: url(${this.getFlagSrc(option.code)})`
    },

    loadPage() {
      this.isSubmitting = true
      window.location.href = this.destinationLocation
    },
  },
}
</script>

<style lang="scss">
@import 'vue-select/src/scss/vue-select.scss';
.overlay-footer-locale {
  cursor: auto;
  display: none;
  padding: 50px;
  background-color: $c-black;
  font-family: $font-family-helvetica-now;

  @media ($mq-full) {
    background-color: rgba($color-black, 0.8);
  }

  &--visible {
    display: block;
    z-index: 10;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &__inner {
    position: relative;
    height: 100%;
  }

  &__content {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 85vw;
    min-width: 300px;
    max-width: 656px;
    transform: translate(-50%, -50%);
    background-color: $c-black;
    font-family: $font-family-helvetica-now;
    text-align: center;

    @media ($mq-large) {
      width: 60vw;
      padding: 50px;
    }
  }

  &__title {
    margin: 18px 18px 36px;
    font-size: $font-size-xlarge;
    line-height: 1.5;
    color: $color-white;
  }

  &__flag {
    display: inline-block;
    position: relative;
    top: 3px;
    right: 4px;
    margin-right: 2px;
    width: 18px;
    height: 18px;
  }

  &__item {
    padding: 2px 0;

    &--separator {
      &:after {
        position: relative;
        top: 6px;
        right: 4px;
        content: '';
        display: block;
        border-bottom: 2px solid $c-grey-light-3;
      }
    }
  }

  &__language,
  &__country {
    margin: 18px auto;
    max-width: 480px;
  }

  &__submit {
    height: 60px;
    margin: 22px auto 26px;
    max-width: 480px;
    width: 100%;
    font-family: $font-family-helvetica-now;
  }

  &__close {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
    padding: 20px;
    color: $c-white;
    cursor: pointer;

    @media ($mq-medium) {
      top: 22px;
      right: 22px;
    }

    @media ($mq-full) {
      display: block;
      top: 0;
      right: 0;
    }

    &--outer {
      display: block;

      @media ($mq-full) {
        display: none;
      }
    }
  }

  .v-select {
    background-color: $c-black;
    color: $c-white;
    border: 1px solid $c-white;
  }

  .vs__dropdown-toggle {
    height: 56px;
    padding: 0 15px;
  }

  .vs__dropdown-menu {
    .overlay-footer-locale__flag {
      margin-right: 10px;
    }
  }

  .vs__selected {
    margin-top: 0;
    font-size: 16px;
    line-height: 1.5;
    color: $c-white;
  }

  .vs--single.vs--open {
    .vs__selected {
      position: static;
    }
  }

  .vs__clear {
    display: none;
  }

  .vs__open-indicator {
    width: 14px;
    height: 14px;
    fill: $c-white;
  }

  .vs__search {
    position: absolute;
    left: 0;
    top: 11px;
    width: 100%;
  }
}

.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity ease-in-out $transition-time-fast;
}

.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}
</style>

<i18n src="@/locales/overlay_footer_locale.json"></i18n>
